/**
 * Formattazione per la visualizzazione.
 *
 * ⚠️ Le date arrivano dal backend come stringhe `AAAA-MM-GG` e vanno
 * formattate **senza passare da `new Date()`**: quella costruttrice le
 * interpreta a mezzanotte UTC e, convertite nel fuso italiano, possono
 * scivolare al giorno prima. Su uno scadenziario un giorno di scarto è un bug
 * che compare solo per certe date e in certi periodi dell'anno — quindi si
 * manifesta in produzione, non nei test.
 */

const MESI = [
  'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre',
];

/** `2026-12-31` → `31/12/2026` */
export function dataBreve(iso: string): string {
  const [anno, mese, giorno] = iso.split('-');
  return `${giorno}/${mese}/${anno}`;
}

/** `2026-12-31` → `31/12` */
export function dataCorta(iso: string): string {
  const [, mese, giorno] = iso.split('-');
  return `${giorno}/${mese}`;
}

/** `2026-12-31` → `31 dicembre 2026` */
export function dataEstesa(iso: string): string {
  const [anno, mese, giorno] = iso.split('-');
  const nomeMese = MESI[Number(mese) - 1] ?? mese;
  return `${Number(giorno)} ${nomeMese} ${anno}`;
}

/**
 * Un istante tecnico → `06/08/2026, 08:24` in ora italiana.
 *
 * ⚠️ Il backend manda i timestamp in **UTC senza dirlo**: `2026-08-06
 * 06:24:59`. Passata così a `new Date()`, JavaScript la interpreta come ora
 * **locale**, e d'estate la schermata mostrerebbe le 06:24 per qualcosa
 * avvenuto alle 08:24. La `Z` che si aggiunge qui sotto è ciò che dichiara
 * "questa è UTC"; la conversione al fuso italiano viene dopo.
 *
 * Vale solo per i timestamp (`inviataIl`, `verificataIl`). Le **date** di
 * scadenza sono giorni, non istanti: per quelle si usa `dataBreve`, che non
 * passa mai da `new Date()`.
 */
/**
 * Da stringa a `Date`, accettando **le due forme** che circolano in questa
 * applicazione.
 *
 * Il database e le rotte restituiscono `2026-08-28 19:18:47`: UTC, ma senza
 * dirlo. Gli script sul server scrivono `2026-08-28T19:18:47Z`, che invece lo
 * dice. Appiccicare una `Z` alla seconda produce `...ZZ`, cioè una data non
 * valida.
 *
 * 🔴 **Questo controllo mancava, e la toppa viveva altrove.** In
 * `pagine/impostazioni/Aggiornamenti.tsx` c'era una `formattaIstante` che
 * toglieva la `Z` a mano prima di chiamare `istante`: funzionava, ma solo per
 * chi la conosceva. L'1 settembre 2026 la colonna «Quando» dello storico ha
 * chiamato `istante` direttamente — come è naturale fare — e ha mostrato per
 * giorni la stringa ISO grezza in tabella, senza che niente sembrasse rotto:
 * `istante` su una data non valida restituisce l'ingresso così com'è. Una
 * seconda toppa nel secondo file avrebbe solo spostato l'appuntamento al terzo.
 */
function analizza(utc: string): Date {
  const conLaT = utc.replace(' ', 'T');
  // Già dichiarato: `Z`, oppure uno scostamento `+02:00` / `-05:00`.
  const dichiaraIlFuso = /(?:Z|[+-]\d{2}:?\d{2})$/.test(conLaT);

  return new Date(dichiaraIlFuso ? conLaT : `${conLaT}Z`);
}

export function istante(utc: string): string {
  const data = analizza(utc);
  if (Number.isNaN(data.getTime())) return utc;

  return new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(data);
}

/**
 * Il giorno di calendario **italiano** di un istante, in `AAAA-MM-GG`.
 *
 * Serve a rispondere a «è successo oggi?» senza passare dai millisecondi:
 * confrontare due istanti a distanza di 24 ore sbaglia due volte l'anno, nelle
 * notti in cui l'ora legale rende una giornata di 23 o 25 ore. Due stringhe di
 * calendario invece non hanno fusi né durate.
 */
function giornoItaliano(data: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(data);
}

/**
 * Quanti giorni di calendario separano due date `AAAA-MM-GG`.
 *
 * L'aritmetica si fa su mezzanotti UTC costruite dai pezzi della stringa: sono
 * per definizione a 24 ore esatte l'una dall'altra, quindi la divisione è
 * esatta e non eredita nessun cambio d'ora.
 */
function giorniFra(primo: string, secondo: string): number {
  const aUtc = (giorno: string): number => {
    const [anno, mese, giornoDelMese] = giorno.split('-').map(Number);
    return Date.UTC(anno ?? 0, (mese ?? 1) - 1, giornoDelMese ?? 1);
  };

  return Math.round((aUtc(secondo) - aUtc(primo)) / 86_400_000);
}

/**
 * Un istante raccontato come lo direbbe una persona: «oggi alle 10:48»,
 * «ieri alle 02:00», «venerdì alle 09:41», «25 agosto alle 09:41».
 *
 * 🎯 **Non sostituisce `istante`, e non deve.** Là dove si confrontano righe
 * fra loro — il registro delle attività, l'elenco degli allegati — serve una
 * colonna che si legga in verticale, e `28/08/2026 21:13` allineato è più
 * veloce da scorrere di frasi tutte diverse. Questa forma serve dove le righe
 * sono poche e ognuna è un fatto a sé, come lo storico degli aggiornamenti:
 * lì «ieri alle 02:00» dice in due parole quello che una data costringe a
 * calcolare.
 *
 * `adesso` è un parametro e non `new Date()` scritto dentro: è ciò che rende
 * la funzione provabile senza far finta di essere un altro giorno.
 *
 * ⚠️ Oltre la settimana il giorno della settimana smette di aiutare — «lunedì»
 * a tre settimane di distanza è più ambiguo di una data — e si passa al mese
 * per esteso. L'anno compare solo quando non è quello corrente: scriverlo
 * sempre sarebbe rumore per undici mesi su dodici.
 */
export function istanteUmano(utc: string, adesso: Date = new Date()): string {
  const data = analizza(utc);
  if (Number.isNaN(data.getTime())) return utc;

  const ora = new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    hour: '2-digit',
    minute: '2-digit',
  }).format(data);

  const distanza = giorniFra(giornoItaliano(data), giornoItaliano(adesso));

  if (distanza === 0) return `oggi alle ${ora}`;
  if (distanza === 1) return `ieri alle ${ora}`;

  // Solo all'indietro e dentro la settimana. Un istante nel futuro non
  // dovrebbe esistere in una cronaca, ma se un orologio storto ne producesse
  // uno, «giovedì» non direbbe se è quello passato o quello che viene.
  if (distanza >= 2 && distanza <= 6) {
    const giorno = new Intl.DateTimeFormat('it-IT', {
      timeZone: 'Europe/Rome',
      weekday: 'long',
    }).format(data);

    return `${giorno} alle ${ora}`;
  }

  const stessoAnno =
    giornoItaliano(data).slice(0, 4) === giornoItaliano(adesso).slice(0, 4);

  const giorno = new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    day: 'numeric',
    month: 'long',
    ...(stessoAnno ? {} : { year: 'numeric' }),
  }).format(data);

  return `${giorno} alle ${ora}`;
}

/** Oggi, in `AAAA-MM-GG`, secondo il fuso italiano. */
export function oggi(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** `-8` → `−8`, `+14` → `+14`. Il segno rende esplicito "già passata". */
export function giorniConSegno(giorni: number): string {
  return giorni < 0 ? `−${Math.abs(giorni)}` : `+${giorni}`;
}

/** Iniziali per l'avatar: "Enrico Testa" → "ET". */
export function iniziali(nome: string): string {
  return nome
    .split(/\s+/)
    .filter((p) => p !== '')
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/*
 * ⚠️ `ETICHETTE_CATEGORIA` non esiste più.
 *
 * Era la mappa dei tre valori dell'ENUM ai loro nomi leggibili. Dal punto 3b
 * le categorie le crea l'ufficio: nome e descrizione arrivano dall'API, e una
 * mappa scritta qui sarebbe sbagliata dal primo giorno in cui qualcuno ne
 * aggiunge una. Chi cerca il nome di una categoria lo trova già pronto dentro
 * `tipo.categoria.nome`.
 */
